import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  X,
  Upload,
  Loader2,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { Button } from "../../Components/Button";
import { Input } from "../../Components/Input";
import { decryptData } from "../../../utils/storageCrypto";

/* -------------------------------------------------------------------------- */
/*                      INTERNAL CUSTOM TOAST COMPONENT                        */
/* -------------------------------------------------------------------------- */
function CustomToast({ toast, onDismiss }) {
  if (!toast) return null;

  const isError = toast.type === "error";

  return (
    <div className="absolute top-5 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-md animate-in fade-in slide-in-from-top-3 duration-200">
      <div
        className={`flex items-start gap-3 p-3.5 rounded-xl border shadow-xl ${
          isError
            ? "bg-red-50 border-red-200 text-red-900"
            : "bg-emerald-50 border-emerald-200 text-emerald-900"
        }`}
      >
        <div className="shrink-0 mt-0.5">
          {isError ? (
            <AlertCircle className="h-5 w-5 text-red-600" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          )}
        </div>

        <div className="flex-1 text-sm font-medium leading-snug">
          {toast.message}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onDismiss}
            className={`p-1 rounded-md hover:bg-black/5 transition text-gray-500`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function CreateRequestModal({ type, onClose, onSuccess }) {
  const backendBaseUrl = process.env.REACT_APP_FORE_BRICKBUCKET;

  /* -------------------------------------------------------------------------- */
  /*                                   STATES                                   */
  /* -------------------------------------------------------------------------- */
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // In-modal Toast State
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  const [ticketNumber, setTicketNumber] = useState("");
  const [searchText, setSearchText] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [customer, setCustomer] = useState(null);

  const [orderItems, setOrderItems] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  const [form, setForm] = useState({
    order_id: "",
    customer_id: "",
    items: [],
    reason_code: "",
    return_mode: "PAID_PICKUP",
    media: [],
  });

  const searchContainerRef = useRef(null);

  /* -------------------------------------------------------------------------- */
  /*                            INTERNAL TOAST TRIGGER                          */
  /* -------------------------------------------------------------------------- */
  const showToast = (message, toastType = "error", action = null, duration = 6000) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);

    setToast({
      message,
      type: toastType,
      action,
    });

    if (duration > 0) {
      toastTimeoutRef.current = setTimeout(() => {
        setToast(null);
      }, duration);
    }
  };

  const dismissToast = () => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast(null);
  };

  /* -------------------------------------------------------------------------- */
  /*                     DISMISS DROPDOWN ON OUTSIDE CLICK                      */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  /* -------------------------------------------------------------------------- */
  /*                                API CALLS                                   */
  /* -------------------------------------------------------------------------- */
  const searchOrders = async (keyword) => {
    if (!keyword.trim()) {
      setOrders([]);
      setShowDropdown(false);
      return;
    }

    try {
      setSearching(true);
      const res = await fetch(
        `${backendBaseUrl}customer/salesorder/get-all-salesorders?status=Out for Delivery,Delivered`
      );
      const json = await res.json();
      const allOrders = json.data || [];

      const filtered = allOrders.filter((order) =>
        (order.quick_buy_order_number || order.salesorder_number || "")
          .toLowerCase()
          .includes(keyword.toLowerCase())
      );

      setOrders(filtered);
      setShowDropdown(true);
    } catch (err) {
      console.error(err);
      setOrders([]);
    } finally {
      setSearching(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      setLoading(true);
      const res = await fetch(
        `${backendBaseUrl}customer/salesorder/get-salesorder?salesorder_id=${orderId}`
      );
      const json = await res.json();

      setCustomer(json.data?.customer_id || null);
      setOrderItems(json.data?.items || []);

      setForm((prev) => ({
        ...prev,
        order_id: orderId,
        customer_id: json.data?.customer_id?._id || "",
        items: [],
      }));
    } catch (err) {
      console.error(err);
      showToast("Failed to load order details.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchReasons = async () => {
    try {
      const res = await fetch(
        `${backendBaseUrl}returns/get-reasons?type=return`
      );
      const json = await res.json();
      setReasonOptions(json.data || []);
    } catch (err) {
      console.error(err);
      setReasonOptions([
        { code: "DAMAGED_PRODUCT", name: "Damaged Product" },
        { code: "WRONG_PRODUCT", name: "Wrong Product" },
        { code: "DEFECTIVE_PRODUCT", name: "Defective Product" },
        { code: "QUALITY_ISSUE", name: "Quality Issue" },
      ]);
    }
  };

  useEffect(() => {
    fetchReasons();
  }, []);

  /* -------------------------------------------------------------------------- */
  /*                                  HELPERS                                   */
  /* -------------------------------------------------------------------------- */
  const debounceSearch = useMemo(() => {
    let timeout;
    return (value) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        searchOrders(value);
      }, 350);
    };
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (val.trim().length >= 2) {
      debounceSearch(val);
    } else {
      setOrders([]);
      setShowDropdown(false);
    }
  };

  const handleSelectOrder = async (order) => {
    const label = order.quick_buy_order_number || order.salesorder_number;
    setSelectedOrder(order);
    setSearchText(label);
    setShowDropdown(false);
    await fetchOrderDetails(order._id);
  };

  const handleResetSelectedOrder = () => {
    setSelectedOrder(null);
    setSearchText("");
    setOrders([]);
    setCustomer(null);
    setOrderItems([]);
    setForm((prev) => ({
      ...prev,
      order_id: "",
      customer_id: "",
      items: [],
    }));
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /* -------------------------------------------------------------------------- */
  /*                              ITEM SELECTION                                */
  /* -------------------------------------------------------------------------- */
  const toggleItem = (item) => {
    setForm((prev) => {
      const exists = prev.items.find((x) => x.item_id === item.item_id);
      if (exists) {
        return {
          ...prev,
          items: prev.items.filter((x) => x.item_id !== item.item_id),
        };
      }
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            item_id: item.item_id,
            item_name: item.item_name,
            requested_qty:
              item.receivable_quantity || item.delivered_quantity || 1,
          },
        ],
      };
    });
  };

  const updateQty = (itemId, qty) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.item_id === itemId
          ? { ...item, requested_qty: Number(qty) }
          : item
      ),
    }));
  };

  const isSelected = (id) => form.items.some((x) => x.item_id === id);

  const getSelectedQty = (id) =>
    form.items.find((x) => x.item_id === id)?.requested_qty ?? 1;

  /* -------------------------------------------------------------------------- */
  /*                               IMAGE HANDLERS                               */
  /* -------------------------------------------------------------------------- */
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* -------------------------------------------------------------------------- */
  /*                               SUBMISSION                                   */
  /* -------------------------------------------------------------------------- */
  const handleSubmit = async () => {
    if (!form.order_id) {
      showToast("Please search and select an order.", "error", {
        label: "Dismiss",
        onClick: dismissToast,
      });
      return;
    }

    if (!form.items.length) {
      showToast("Please select at least one item.", "error", {
        label: "Dismiss",
        onClick: dismissToast,
      });
      return;
    }

    if (!form.reason_code) {
      showToast("Please select a reason.", "error", {
        label: "Dismiss",
        onClick: dismissToast,
      });
      return;
    }

    if (type === "return" && !form.return_mode) {
      showToast("Please select a return mode.", "error", {
        label: "Dismiss",
        onClick: dismissToast,
      });
      return;
    }

    for (const item of form.items) {
      if (!item.requested_qty || Number(item.requested_qty) <= 0) {
        showToast("Invalid requested quantity on selected items.", "error", {
          label: "Dismiss",
          onClick: dismissToast,
        });
        return;
      }
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("ticket_number", ticketNumber.trim());
      formData.append("order_id", form.order_id);
      formData.append("customer_id", form.customer_id);
      formData.append("reason_code", form.reason_code);
      formData.append("return_mode", form.return_mode);
      formData.append("items", JSON.stringify(form.items));

      imageFiles.forEach((file) => {
        formData.append("media", file);
      });

      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const endpoint =
        type === "return"
          ? `${backendBaseUrl}returns/request-return`
          : `${backendBaseUrl}returns/request-replacement`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "x-token": token,
        },
        body: formData,
      });

      const json = await response.json().catch(() => ({}));

      if (!response.ok || json.success === false) {
        const errorMsg =
          json.message ||
          json.error ||
          `Request failed with status code ${response.status}`;

        showToast(
          errorMsg,
          "error",
          {
            label: "Close",
            onClick: dismissToast,
          },
          8000
        );
        return;
      }

      showToast(json.message || "Request submitted successfully.", "success", null, 2000);

      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
        }
      }, 1000);
    } catch (err) {
      console.error(err);
      showToast(
        err?.message || "Unable to submit the request.",
        "error",
        {
          label: "Close",
          onClick: dismissToast,
        },
        8000
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[92vh] max-h-[900px] flex flex-col overflow-hidden">
        
        {/* CUSTOM IN-MODAL TOAST NOTIFICATION */}
        <CustomToast toast={toast} onDismiss={dismissToast} />

        {/* -------------------- HEADER (Fixed) -------------------- */}
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Raise {type === "return" ? "Return" : "Replacement"} Request
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Select the sales order, choose items, and enter details.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={saving}>
            <X className="h-5 w-5 text-gray-500" />
          </Button>
        </div>

        {/* -------------------- SCROLLABLE CONTENT BODY -------------------- */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">

          {/* TOP GRID: ORDER SEARCH & TICKET NUMBER */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* ENHANCED ORDER SEARCH */}
            <div className="relative" ref={searchContainerRef}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 block">
                  Search Order <span className="text-red-500">*</span>
                </label>
                {selectedOrder && (
                  <button
                    type="button"
                    onClick={handleResetSelectedOrder}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                  >
                    <RefreshCw className="h-3 w-3" /> Change Order
                  </button>
                )}
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  className={`pl-9 pr-9 h-10 text-sm ${
                    selectedOrder ? "border-green-500 bg-green-50/40 font-medium" : ""
                  }`}
                  placeholder="Type Order / Sales Number (min 2 chars)..."
                  value={searchText}
                  disabled={Boolean(selectedOrder)}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (orders.length > 0) setShowDropdown(true);
                  }}
                />

                {searching && (
                  <Loader2 className="absolute right-3 top-3 h-4 w-4 text-gray-400 animate-spin" />
                )}

                {selectedOrder && (
                  <CheckCircle2 className="absolute right-3 top-3 h-4 w-4 text-green-600" />
                )}
              </div>

              {/* SEARCH RESULTS DROPDOWN */}
              {showDropdown && orders.length > 0 && !selectedOrder && (
                <div className="absolute left-0 right-0 top-[68px] bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto divide-y divide-gray-100">
                  {orders.map((order) => {
                    const label =
                      order.quick_buy_order_number || order.salesorder_number;
                    return (
                      <button
                        key={order._id}
                        type="button"
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between transition-colors"
                        onClick={() => handleSelectOrder(order)}
                      >
                        <div>
                          <p className="font-semibold text-sm text-gray-800">
                            {label}
                          </p>
                          <p className="text-xs text-gray-500">
                            {order.customer_id?.customer_name || "Unknown Customer"}
                          </p>
                        </div>
                        <span className="text-[11px] bg-blue-50 text-blue-700 font-medium px-2 py-0.5 rounded-full border border-blue-200">
                          {order.status || "Ready"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {showDropdown && orders.length === 0 && !searching && searchText.length >= 2 && (
                <div className="absolute left-0 right-0 top-[68px] bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-4 text-center text-xs text-gray-500">
                  No delivered/dispatched orders found matching "{searchText}".
                </div>
              )}
            </div>

            {/* TICKET NUMBER INPUT (OPTIONAL) */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5 block">
                Ticket Number <span className="text-gray-400 font-normal lowercase">(optional)</span>
              </label>
              <Input
                placeholder="Enter or paste ticket number (e.g. TIK-9921)"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                className="h-10 text-sm font-medium"
              />
            </div>

          </div>

          {/* CUSTOMER CARD */}
          {customer && (
            <div className="rounded-xl border border-gray-200 bg-slate-50/70 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <span className="text-[11px] uppercase font-bold text-gray-400 block">
                    Customer Name
                  </span>
                  <p className="font-semibold text-gray-800 text-sm mt-0.5">
                    {customer.customer_name || "—"}
                  </p>
                </div>
                <div>
                  <span className="text-[11px] uppercase font-bold text-gray-400 block">
                    Contact Number
                  </span>
                  <p className="text-sm font-medium text-gray-700 mt-0.5">
                    {customer.mobile_number || "—"}
                  </p>
                </div>
                <div>
                  <span className="text-[11px] uppercase font-bold text-gray-400 block">
                    Email Address
                  </span>
                  <p className="text-sm font-medium text-gray-700 mt-0.5 truncate">
                    {customer.email || "—"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ITEMS SELECTION */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 border rounded-xl bg-gray-50/50">
              <Loader2 className="h-7 w-7 text-gray-400 animate-spin mb-2" />
              <p className="text-xs text-gray-500">Loading order items...</p>
            </div>
          ) : orderItems.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700">
                  Select Items for {type === "return" ? "Return" : "Replacement"} *
                </h3>
                <span className="text-xs text-gray-500">
                  {form.items.length} of {orderItems.length} selected
                </span>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 border-b text-[11px] uppercase text-gray-500 font-semibold">
                    <tr>
                      <th className="px-4 py-3 w-12 text-center">Select</th>
                      <th className="px-4 py-3">Product Name</th>
                      <th className="px-4 py-3 text-center">Ordered</th>
                      <th className="px-4 py-3 text-center">Delivered</th>
                      <th className="px-4 py-3 w-36 text-center">Request Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orderItems.map((item) => {
                      const maxQty =
                        item.receivable_quantity || item.delivered_quantity || item.quantity;
                      const checked = isSelected(item.item_id);

                      return (
                        <tr
                          key={item.item_id}
                          className={`hover:bg-gray-50 transition-colors ${
                            checked ? "bg-blue-50/30" : ""
                          }`}
                        >
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                              checked={checked}
                              onChange={() => toggleItem(item)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-gray-800 leading-tight">
                              {item.item_name}
                            </p>
                            {item.sku && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                SKU: {item.sku}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center font-medium text-gray-600">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-3 text-center font-medium text-gray-600">
                            {maxQty}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {checked ? (
                              <Input
                                type="number"
                                min={1}
                                max={maxQty}
                                value={getSelectedQty(item.item_id)}
                                onChange={(e) =>
                                  updateQty(item.item_id, e.target.value)
                                }
                                className="h-8 text-center text-sm w-24 mx-auto"
                              />
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : selectedOrder && (
            <div className="p-4 border rounded-xl text-center text-xs text-gray-500 bg-gray-50">
              No line items found for this sales order.
            </div>
          )}

          {/* REASON & RETURN MODE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5 block">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg h-10 px-3 text-sm text-gray-800 outline-none focus:border-blue-500"
                value={form.reason_code}
                onChange={(e) => handleChange("reason_code", e.target.value)}
              >
                <option value="">Select a reason</option>
                {reasonOptions.map((reason) => (
                  <option key={reason.code} value={reason.code}>
                    {reason.label || reason.name}
                  </option>
                ))}
              </select>
            </div>

            {type === "return" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5 block">
                  Return Mode <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg h-10 px-3 text-sm text-gray-800 outline-none focus:border-blue-500"
                  value={form.return_mode}
                  onChange={(e) => handleChange("return_mode", e.target.value)}
                >
                  <option value="PAID_PICKUP">Paid Pickup</option>
                  <option value="HUB_DROP">Hub Drop</option>
                </select>
              </div>
            )}
          </div>

          {/* ATTACH MEDIA */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5 block">
              Proof Photos / Media
            </label>
            <label
              htmlFor="request-media"
              className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-5 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <Upload className="h-6 w-6 text-gray-400 mb-1.5" />
              <p className="text-sm font-semibold text-gray-700">
                Click or drag images to upload
              </p>
              <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, JPEG formats</p>
            </label>
            <input
              id="request-media"
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />

            {/* PREVIEWS */}
            {imageFiles.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mt-3">
                {imageFiles.map((file, idx) => (
                  <div
                    key={idx}
                    className="relative border rounded-lg overflow-hidden group aspect-square bg-gray-100"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-red-600/90 text-white rounded-full p-1 shadow hover:bg-red-700 transition"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SUMMARY STRIP */}
          <div className="rounded-xl bg-slate-50 border border-gray-200 p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center sm:text-left">
              <div>
                <span className="text-[11px] uppercase font-bold text-gray-400">
                  Selected Items
                </span>
                <p className="text-lg font-bold text-gray-800">
                  {form.items.length}
                </p>
              </div>
              <div>
                <span className="text-[11px] uppercase font-bold text-gray-400">
                  Total Qty
                </span>
                <p className="text-lg font-bold text-gray-800">
                  {form.items.reduce(
                    (acc, curr) => acc + Number(curr.requested_qty || 0),
                    0
                  )}
                </p>
              </div>
              <div>
                <span className="text-[11px] uppercase font-bold text-gray-400">
                  Request Type
                </span>
                <p className="text-sm font-bold text-blue-600 mt-1 uppercase">
                  {type}
                </p>
              </div>
              <div>
                <span className="text-[11px] uppercase font-bold text-gray-400">
                  Photos Attached
                </span>
                <p className="text-lg font-bold text-gray-800">
                  {imageFiles.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* -------------------- FOOTER (Sticky & Always Visible) -------------------- */}
        <div className="border-t border-gray-200 px-6 py-3.5 bg-white shrink-0 flex items-center justify-end gap-3 z-20">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="text-gray-700 font-medium px-4 h-9"
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={saving || !form.order_id}
            className="bg-[#f7525a] hover:bg-[#d9444b] text-white font-semibold px-5 h-9"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit {type === "return" ? "Return" : "Replacement"}
          </Button>
        </div>

      </div>
    </div>
  );
}